import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Button } from '../../components/ui/button';
import QuizComponent from '../../components/QuizComponent';
import { FlaskConical, BookOpen, Eye, Lightbulb, Target, AlertTriangle, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const exegesisLabs = [
  {
    id: '1john1:9',
    title: '1 John 1:9 – "If We Confess Our Sins"',
    passage: 'If we confess our sins, he is faithful and just to forgive us our sins and to cleanse us from all unrighteousness.',
    reference: '1 John 1:9 ESV',
    commonly_taught: 'Christians must confess sins to be cleansed and restored to fellowship.',
    steps: [
      {
        title: 'The Context Problem',
        content: `Many Christians use this verse as a "bar of soap"—confess to get cleansed, maintain fellowship with God. But this interpretation contradicts clear New Testament teaching.

**Contradiction #1:** Hebrews 10:14 says believers are "perfected FOR ALL TIME." If we're already perfected, why would we need repeated cleansing?

**Contradiction #2:** Colossians 2:13-14 says God "forgave us ALL our trespasses." If ALL trespasses are already forgiven, why ask for forgiveness after each sin?

**Contradiction #3:** Ephesians 1:7 says "In him we HAVE redemption... the forgiveness of our trespasses." We HAVE it—present tense possession.

Something doesn't fit. Let's look at context.`
      },
      {
        title: 'Who Is John Addressing?',
        content: `First John was written to combat Gnostic false teachers who:
• Claimed special spiritual knowledge
• Denied they had sin ("If we say we have no sin" - 1:8)
• Denied they had sinned ("If we say we have not sinned" - 1:10)

John is not writing to mature Christians about maintaining fellowship. He's addressing people who DENY HAVING SIN at all.

Look at verses 6-10 as a unit:
• v.6 - "If we say we have fellowship..." (testing a claim)
• v.8 - "If we say we have no sin..." (testing a claim)
• v.10 - "If we say we have not sinned..." (testing a claim)

And sandwiched between: v.9 - "If we confess our sins..." (the genuine response)

This is evangelistic: Acknowledge your sin and receive salvation—vs. Gnostics who denied having sin at all.`
      },
      {
        title: 'The Grace Interpretation',
        content: `1 John 1:9 is about INITIAL confession for salvation, not ongoing confession for maintenance.

**For unbelievers:** Acknowledge your sin → Receive forgiveness and cleansing (salvation)
**For believers:** You are already forgiven ALL trespasses (Colossians 2:13-14), perfected forever (Hebrews 10:14), and have redemption as a present possession (Ephesians 1:7).

Does this mean believers never acknowledge sin? No! But our acknowledgment is:
• Agreement with God ("Yes, that was sin")
• NOT requesting what we already have
• Thanking Him we're already forgiven
• Trusting Christ's life in us

**The switch:** Instead of "God, please forgive me," say "Father, thank You that I'm already forgiven. I trust Your life in me."`
      },
      {
        title: 'Anchoring Scriptures',
        content: `Always interpret unclear passages with clear ones:

**Colossians 2:13-14** - "Having forgiven us ALL our trespasses, by canceling the record of debt that stood against us with its legal demands. This he set aside, nailing it to the cross."

**Hebrews 10:14** - "For by a single offering he has perfected for all time those who are being sanctified."

**Hebrews 10:17-18** - "I will remember their sins and their lawless deeds no more. Where there is forgiveness of these, there is no longer any offering for sin."

**Ephesians 1:7** - "In him we HAVE redemption through his blood, the forgiveness of our trespasses."

The clear teaching: ALL forgiven. PERFECTED forever. No more offering needed. This must guide our reading of 1 John 1:9.`
      }
    ]
  },
  {
    id: 'hebrews-warnings',
    title: 'Hebrews Warnings – Chapters 6 & 10',
    passage: 'For it is impossible, in the case of those who have once been enlightened... and then have fallen away, to restore them again to repentance.',
    reference: 'Hebrews 6:4-6 ESV',
    commonly_taught: 'Christians can lose their salvation if they fall away.',
    steps: [
      {
        title: 'The Audience Question',
        content: `Who was Hebrews written to? The answer is in the name: HEBREWS—Jews who had professed faith in Christ but were considering returning to temple Judaism.

The book repeatedly contrasts:
• Angels vs. Christ (ch. 1-2)
• Moses vs. Christ (ch. 3)
• Joshua's rest vs. Christ's rest (ch. 4)
• Levitical priesthood vs. Christ's priesthood (ch. 5-7)
• Old Covenant vs. New Covenant (ch. 8-10)
• Old sacrifices vs. Christ's once-for-all sacrifice (ch. 9-10)

The argument: Why go back to shadows when you have the reality? Why return to animal sacrifices when Christ's sacrifice is complete?`
      },
      {
        title: 'The Warning Explained',
        content: `The warnings are about REJECTING Christ entirely—not about Christians struggling with sin.

**Hebrews 6:4-6:** "Impossible... to restore them again to repentance, since they are crucifying once again the Son of God."

This describes apostates who:
• Had exposure to truth ("enlightened," "tasted")
• Now completely reject Christ
• Essentially say: "Christ's sacrifice was worthless"

If Christ's sacrifice is rejected, no other sacrifice exists. The warning is hypothetical—showing the impossibility of a Plan B if Christ is rejected.

**Hebrews 10:26:** "If we go on sinning deliberately after receiving the knowledge of the truth, there no longer remains a sacrifice for sins."

The "deliberate sin" is rejecting Christ to return to animal sacrifices. For those who reject Christ's sacrifice, indeed no other sacrifice remains.`
      },
      {
        title: 'What About True Believers?',
        content: `The same book that contains these warnings also contains the strongest security statements:

**Hebrews 10:10** - "We HAVE BEEN sanctified through the offering of the body of Jesus Christ once for all."

**Hebrews 10:14** - "For by a single offering he has PERFECTED FOR ALL TIME those who are being sanctified."

**Hebrews 10:17-18** - "I will remember their sins and their lawless deeds NO MORE. Where there is forgiveness of these, there is NO LONGER any offering for sin."

The warnings are for those considering rejecting Christ. The assurances are for those IN Christ. True believers are perfected forever, sins remembered no more.`
      },
      {
        title: 'Anchoring Scriptures',
        content: `Hebrews itself anchors the security of believers:

**Hebrews 7:25** - "He is able to save to the uttermost those who draw near to God through him, since he always lives to make intercession for them."

**Hebrews 10:14** - "Perfected for all time"

**Hebrews 10:17** - "Sins remembered no more"

Plus Jesus' own words:

**John 10:28-29** - "I give them eternal life, and they will never perish, and no one will snatch them out of my hand. My Father, who has given them to me, is greater than all, and no one is able to snatch them out of the Father's hand."

The warnings are real—for those rejecting Christ. The security is real—for those in Christ.`
      }
    ]
  },
  {
    id: 'parables-rewards',
    title: 'Parables of Minas/Talents – Binary Outcomes',
    passage: 'His master said to him, "Well done, good and faithful servant... Enter into the joy of your master."',
    reference: 'Matthew 25:21 ESV',
    commonly_taught: 'Christians will receive different levels of rewards in heaven based on works.',
    steps: [
      {
        title: 'The Graded Rewards Question',
        content: `Many teach that the parables of the Minas (Luke 19) and Talents (Matthew 25) show Christians will have different levels of reward in heaven. Some will rule 10 cities, others 5. Some will have more treasure, others less.

But look more carefully at the outcomes...`
      },
      {
        title: 'Binary, Not Graded',
        content: `The outcomes in these parables are actually BINARY—not graded:

**Matthew 25 (Talents):**
• Faithful servants: "Enter into the joy of your master" (v.21, 23)
• Unfaithful servant: "Cast the worthless servant into the outer darkness" (v.30)

The first two servants receive the SAME commendation and SAME invitation—"Enter into the joy." The third is cast out entirely.

This isn't about Christians getting different levels of heaven. It's about:
• Faith → Enters joy (salvation)
• Unbelief → Cast out (judgment)

The varying amounts (10 talents, 5 talents) show different opportunities/stewardship, but the OUTCOME is binary: joy or outer darkness.`
      },
      {
        title: 'What About "Well Done"?',
        content: `"Well done, good and faithful servant" is not grading performance—it's welcoming believers into glory.

Note: Both the 10-talent and 5-talent servants receive the EXACT SAME commendation. Word for word. They don't get different rewards based on how much they produced.

The parable's point is: genuine faith produces fruit, and that faith—however it's expressed—receives full welcome into the Master's joy.

The contrast is with the third servant who had NO faith (he buried his talent out of fear and wrong views of the Master). He represents unbelievers who reject what God has given.`
      },
      {
        title: 'The Inheritance IS the Reward',
        content: `Colossians 3:24 clarifies: "from the Lord you will receive the inheritance as your reward."

The INHERITANCE is the reward. Not levels of inheritance. Not grades of heaven. The inheritance itself.

All believers share:
• Same forgiveness (Ephesians 1:7)
• Same union with Christ (1 Corinthians 6:17)
• Same completeness (Colossians 2:10)
• Same inheritance (Colossians 3:24)
• Same entrance into joy (Matthew 25:21)

The New Covenant doesn't grade believers. It declares them all equally complete in Christ.`
      }
    ]
  }
];

export default function ExegesisHub() {
  const [scaryVerses, setScaryVerses] = useState([]);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [verseData, setVerseData] = useState(null);

  useEffect(() => {
    const fetchScaryVerses = async () => {
      try {
        const response = await axios.get(`${API}/scary-verses`);
        setScaryVerses(response.data);
      } catch (error) {
        console.error('Failed to fetch scary verses:', error);
      }
    };
    fetchScaryVerses();
  }, []);

  const handleSelectVerse = async (verseId) => {
    setSelectedVerse(verseId);
    try {
      const response = await axios.get(`${API}/scary-verses/${verseId}`);
      setVerseData(response.data);
    } catch (error) {
      console.error('Failed to fetch verse data:', error);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)]" data-testid="exegesis-hub">
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1637109012034-e2b6b32c61c4?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Bible open on coffee table representing deep study"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hub-hero-overlay flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 w-full">
            <span className="inline-block px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm mb-3">
              Step 4 · Practice
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              Exegesis Labs
            </h1>
            <p className="text-white/80 mt-2 max-w-2xl">
              Tough texts through the grace lens
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="labs" className="space-y-8">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="labs" data-testid="tab-labs">Study Labs</TabsTrigger>
            <TabsTrigger value="switchboard" data-testid="tab-switchboard">Verse Help</TabsTrigger>
            <TabsTrigger value="quiz" data-testid="tab-quiz">Quiz</TabsTrigger>
          </TabsList>

          <TabsContent value="labs" className="space-y-8">
            {/* Introduction */}
            <div className="max-w-3xl">
              <p className="text-lg text-muted-foreground leading-relaxed">
                These labs walk through commonly misunderstood passages—showing how they align with 
                total forgiveness, permanent union, and the finished work when read in proper context.
              </p>
            </div>

            {/* Exegesis Labs */}
            <div className="space-y-8">
              {exegesisLabs.map((lab) => (
                <Card key={lab.id} className="overflow-hidden">
                  <CardHeader className="bg-muted/50">
                    <div className="flex items-center gap-2 mb-2">
                      <FlaskConical className="h-5 w-5 text-primary" strokeWidth={1.5} />
                      <CardTitle className="font-serif">{lab.title}</CardTitle>
                    </div>
                    <CardDescription className="italic text-base">
                      "{lab.passage}"
                    </CardDescription>
                    <p className="text-sm text-primary font-medium mt-2">— {lab.reference}</p>
                    <div className="mt-3 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                      <p className="text-sm flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                        <span><strong>Commonly taught:</strong> {lab.commonly_taught}</span>
                      </p>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Accordion type="single" collapsible className="divide-y">
                      {lab.steps.map((step, index) => (
                        <AccordionItem key={index} value={`${lab.id}-${index}`} className="border-0">
                          <AccordionTrigger className="px-6 hover:no-underline">
                            <span className="flex items-center gap-3">
                              <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                                {index + 1}
                              </span>
                              <span className="font-semibold">{step.title}</span>
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="px-6 pb-6">
                            <div className="pl-11 prose prose-slate max-w-none">
                              {step.content.split('\n\n').map((paragraph, pIndex) => (
                                <p key={pIndex} className="text-foreground/80 leading-relaxed mb-4 last:mb-0 whitespace-pre-wrap">
                                  {paragraph}
                                </p>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="switchboard" className="space-y-6">
            <div className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold mb-4">Scary Verse Switchboard</h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Select a passage that troubles you, and see how it aligns with grace when properly understood.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="md:col-span-1">
                <CardHeader>
                  <CardTitle className="text-base">Select a Passage</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {scaryVerses.map((verseId) => (
                    <Button
                      key={verseId}
                      variant={selectedVerse === verseId ? "default" : "outline"}
                      className="w-full justify-start"
                      onClick={() => handleSelectVerse(verseId)}
                      data-testid={`scary-verse-${verseId}`}
                    >
                      {verseId.replace(/([a-z])(\d)/g, '$1 $2').replace(':', ' ')}
                      <ChevronRight className="h-4 w-4 ml-auto" />
                    </Button>
                  ))}
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                {verseData ? (
                  <>
                    <CardHeader>
                      <CardTitle className="font-serif">{verseData.reference}</CardTitle>
                      <CardDescription className="italic text-base">
                        "{verseData.verse}"
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-primary" />
                          Context
                        </h4>
                        <p className="text-foreground/80">{verseData.context}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <Lightbulb className="h-4 w-4 text-primary" />
                          Grace Truth
                        </h4>
                        <p className="text-foreground/80">{verseData.grace_truth}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <Target className="h-4 w-4 text-primary" />
                          Anchor Scriptures
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {verseData.anchors.map((anchor) => (
                            <span key={anchor} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                              {anchor}
                            </span>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </>
                ) : (
                  <CardContent className="p-8 text-center text-muted-foreground">
                    <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Select a passage to see the grace perspective</p>
                  </CardContent>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="quiz">
            <div className="max-w-2xl mx-auto">
              <QuizComponent hubId="exegesis" />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
